import { Component, Input, Output, EventEmitter } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { DiscussionApiService } from '@angular-workspace/services';
import { KeycloakService } from 'keycloak-angular';

export interface DiscussionItem {
  id: string;
  title: string;
  description: string;
  content?: string;
  author: string;
  authorAvatar?: string;
  category: string;
  /** Discussion channel/type from API (e.g. GETTING_STARTED, PUBLIC) — shown as badge beside category chip */
  discussionType?: string;
  replies: number;
  views: number;
  lastActivity: string;
  /** Formatted date for display (e.g. "Feb 11, 2026") */
  formattedDate?: string;
  /** Status label for badge (e.g. "Approved", "Pending", "Rejected") */
  statusLabel?: string;
  /** Raw status for styling (APPROVED, PENDING, REJECTED, CHANGES_REQUIRED) */
  status?: string;
  isPinned?: boolean;
  isBookmarked?: boolean;
  isVoted?: boolean;
  assetType?: 'Dataset' | 'Model' | 'Usecase';
  assetName?: string;
  tags?: string[];
  attachments?: any[];
}

@Component({
  selector: 'app-discussion-list',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './discussion-list.component.html',
  styleUrl: './discussion-list.component.scss',
})
export class DiscussionListComponent {
  @Input() discussions: DiscussionItem[] = [];
  @Input() isLoggedIn: boolean = false;
  @Input() categoryName: string = ''; // Category name for empty state message
  /** When true, show delete button (for owner's discussions, e.g. My Discussions page) */
  @Input() showDeleteForOwner: boolean = false;
  /** When true, show edit button only for CHANGES_REQUIRED or PENDING status (e.g. Post Submission page) */
  @Input() showEditOnlyForChangesRequired: boolean = false;
  /** When true, hide status badge on cards (e.g. when tab already indicates status) */
  @Input() hideStatusBadge: boolean = false;
  @Output() bookmarkClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();
  @Output() pinClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();
  @Output() editClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();
  @Output() deleteClick = new EventEmitter<{ event: Event; discussion: DiscussionItem }>();

  constructor(
    private router: Router,
    private discussionApiService: DiscussionApiService,
    private keycloakService: KeycloakService
  ) {}

  navigateToDiscussion(discussionId: string): void {
    this.discussionApiService.getReview(discussionId).then(() => {
      this.router.navigate(['/discussions', discussionId]);
    }).catch(() => {
      // Still navigate on error so user can reach the detail page
      this.router.navigate(['/discussions', discussionId]);
    });
  }

  onBookmarkClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.bookmarkClick.emit({ event, discussion });
  }

  onPinClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.pinClick.emit({ event, discussion });
  }

  onEditClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.editClick.emit({ event, discussion });
  }

  onDeleteClick(event: Event, discussion: DiscussionItem): void {
    event.stopPropagation();
    this.deleteClick.emit({ event, discussion });
  }

  /** Raw discussion type from API for badge text. */
  getDiscussionTypeDisplayLabel(value: string): string {
    if (!value) return '';
    return String(value).trim();
  }

  /** Map API category to display label. */
  getCategoryDisplayLabel(category: string): string {
    if (!category) return '';
    const normalized = category.replace(/[\s_-]/g, '').toUpperCase();
    if (normalized === 'DATABANKS' || normalized.includes('DATABANK') || normalized === 'DATA_BANKS') return 'Datasets';
    if (normalized === 'AIMODELS' || normalized === 'AIMODEL' || normalized === 'AI_MODELS' || normalized === 'AI_MODEL') return 'Models';
    if (normalized === 'USECASES' || normalized === 'USECASE' || normalized === 'APPS' || normalized === 'APP') return 'Usecase';
    return category;
  }

  formatRelativeTime(dateString: string): string {
    const dateObj = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - dateObj.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    
    return dateObj.toLocaleDateString();
  }
}
